import { get } from 'svelte/store';
import * as PROXY from '../../../common/api.js';
import * as PEGASUS from '@smatch/pegasus-api-docs';
import { ORGANIZATION_STORE, ORGANIZATION_HASH } from './store.js';
import { ORGANIZATION_KEY } from '../../authentication/services/store.js';
import * as SESSION_API from '../../authentication/services/api.js';
import { addLoader, removeLoader } from '../../../stores/loading.js';
import { addMessage } from '../../../stores/messages.js';
export * from './types.js';
// Initializing
const DOMAIN = import.meta.env.VITE_DOMAIN;
const UNACCEPTABLE_MANAGER_ERROR_MESSAGE = 'Please make sure the user you choose as manager has an admin role and no missing information on their profile.';
// Store Functions
const updateStore = (response, ORGANIZATION_HASH = null) => {
    if ('_' in response) {
        PROXY.updateStore(ORGANIZATION_STORE, ORGANIZATION_HASH, response);
        void SESSION_API.sync({ organizationKey: response._.key });
    }
    return response;
};
// Custom Handlers
/* eslint-disable max-lines-per-function, max-statements */
const handleCreate = async (body) => {
    const OPTIONS = PROXY.getOptions();
    const KEY = addLoader();
    const RESPONSE = await PEGASUS.createOrganization(body, OPTIONS);
    removeLoader(KEY);
    const { statusCode: STATUS_CODE, data: DATA } = RESPONSE;
    const IS_UNACCEPTABLE_MANAGER = 'errors' in DATA
        && Array.isArray(DATA.errors)
        && DATA.errors
            .filter((error) => error.code === 'unacceptable_manager').length > 0;
    if (IS_UNACCEPTABLE_MANAGER) {
        addMessage({
            description: UNACCEPTABLE_MANAGER_ERROR_MESSAGE,
            heading: 'Unacceptable Manager',
            type: 'error',
        });
        return DATA;
    }
    if (STATUS_CODE !== 'STATUS_201') {
        PROXY.handleApiError(STATUS_CODE);
        return DATA;
    }
    return DATA;
};
const handleUpdate = async (keys, body) => {
    const OPTIONS = PROXY.getOptions();
    const KEY = addLoader();
    const RESPONSE = await PEGASUS.updateOrganization(keys, body, OPTIONS);
    removeLoader(KEY);
    const { statusCode: STATUS_CODE, data: DATA } = RESPONSE;
    const IS_UNACCEPTABLE_MANAGER = 'errors' in DATA
        && Array.isArray(DATA.errors)
        && DATA.errors
            .filter((error) => error.code === 'unacceptable_manager').length > 0;
    if (IS_UNACCEPTABLE_MANAGER) {
        addMessage({
            description: UNACCEPTABLE_MANAGER_ERROR_MESSAGE,
            heading: 'Unacceptable Manager',
            type: 'error',
        });
        return DATA;
    }
    if (STATUS_CODE !== 'STATUS_200') {
        PROXY.handleApiError(STATUS_CODE);
        return DATA;
    }
    return DATA;
};
/* eslint-enable */
/* eslint-disable arrow-body-style */
// Filter Organizations
export const filterOrganizations = async (parameters = {}) => {
    // @ts-expect-error - TODO: Fix Pegasus types
    return PROXY.handleFilter(PEGASUS.filterOrganizations, parameters)
        .then(organizations => PROXY.updateStoreBulk(ORGANIZATION_STORE, organizations));
};
// Filter Mini Organizations
export const filterMiniOrganizations = async (parameters = {}) => {
    return PROXY.handleFilter(PEGASUS.filterMiniOrganizations, parameters);
};
// List Available Organization Filters
export const listOrganizationFilters = async (parameters = {}) => {
    return PROXY.handleFetchQuery(PEGASUS.filterOrganizationFilters, parameters);
};
// Fetch Organization
export const fetchOrganization = async (keys, onError) => {
    if (DOMAIN !== 'internal') {
        keys.organizationKey = get(ORGANIZATION_KEY) ?? '';
    }
    // @ts-expect-error - TODO: Fix Pegasus types
    return PROXY.handleFetch(PEGASUS.fetchOrganization, keys)
        .then(({ data, statusCode }) => {
        if (onError?.(statusCode) === true) {
            return {};
        }
        if (statusCode !== 'STATUS_200') {
            PROXY.handleApiError(statusCode);
            return {};
        }
        return PROXY.updateStore(ORGANIZATION_STORE, null, data);
    });
};
// Create Organization
export const createOrganization = async (body) => {
    return handleCreate(body).then(response => updateStore(response, ORGANIZATION_HASH));
};
// Update Organization
// eslint-disable-next-line max-len
export const updateOrganization = async (keys, body) => {
    return handleUpdate(keys, body).then(response => updateStore(response, ORGANIZATION_HASH));
};
// Delete Organization
export const deleteOrganization = async (keys) => {
    return PROXY.handleDelete(PEGASUS.deleteOrganization, keys)
        .then(response => PROXY.removeFromStore(ORGANIZATION_STORE, ORGANIZATION_HASH, keys.organizationKey, response));
};
// Update Organization State
// eslint-disable-next-line max-len
export const updateOrganizationState = async (keys, body) => {
    return PROXY.handleUpdate(PEGASUS.updateOrganizationState, keys, body)
        .then(response => updateStore(response, ORGANIZATION_HASH));
};
/* eslint-enable arrow-body-style */ 
